<template>
  <div class="points-settings-container">
    <h1 class="page-title">积分设置</h1>
    
    <!-- 积分规则设置 -->
    <el-card v-loading="loading" element-loading-text="加载中..." class="content-card">
      <template #header>
        <div class="card-header">
          <h2>积分规则设置</h2>
        </div>
      </template>
      <div class="card-content">
        <el-form :model="settings" label-width="200px" class="member-form">
          <el-divider>积分获取规则</el-divider>
          
          <!-- 价格说明 -->
          <div class="rule-description">
            <strong>积分获取规则说明：</strong>积分比例表示消费金额按对应百分比转换为积分，例如消费100元，若比例为80%，则赠送80积分；
          </div>
          
          <!-- 消费积分规则 -->
          <el-row :gutter="20">
            <el-col :span="10" v-if="false">
              <el-form-item label="房费积分比例">
                <div class="input-with-suffix">
                  <el-input-number 
                    v-model="settings.room_fee_point_ratio"
                    class="suffix-input"
                    :min="0" 
                    :precision="0" 
                    :step="1" 
                    placeholder="请输入手工录入房费积分比例">
                  </el-input-number>
                  <span class="input-suffix">%</span>
                </div>
              </el-form-item>
            </el-col>
            <el-col :span="10">
              <el-form-item label="积分审核积分比例">
                <div class="input-with-suffix">
                  <el-input-number 
                    v-model="settings.system_consumption_point_ratio"
                    class="suffix-input"
                    :min="0" 
                    :precision="0" 
                    :step="1" 
                    placeholder="请输入积分审核积分比例">
                  </el-input-number>
                  <span class="input-suffix">%</span>
                </div>
              </el-form-item>
            </el-col>
            <el-col :span="10">
              <el-form-item label="每日签到积分">
                <el-input-number 
                  v-model="settings.daily_checkin_points"
                  :min="0" 
                  :precision="0" 
                  :step="1" 
                  placeholder="请输入每日签到积分">
                  <template #append>积分</template>
                </el-input-number>
              </el-form-item>
            </el-col>
          </el-row>
          
          <el-divider>积分使用规则</el-divider>
          
          <!-- 积分抵现规则 -->
          <!-- <el-row :gutter="20">
            <el-col :span="10">
              <el-form-item label="积分商城抵扣比例">
                <div class="ratio-input-container">
                  <div class="ratio-input-wrapper">
                    <el-input-number 
                      v-model="mallDiscountPoints"
                      :min="1" 
                      :precision="0" 
                      :step="1" 
                      placeholder="积分">
                    </el-input-number>
                    <span class="ratio-separator">:</span>
                    <span class="fixed-amount">1</span>
                  </div>
                  <div class="ratio-description">
                    例如：10:1 表示 10 积分可抵扣 1 元
                  </div>
                </div>
              </el-form-item>
            </el-col>
            <el-col :span="10">
              <el-form-item label="积分使用最低门槛">
                <el-input-number 
                  v-model="settings.mall_minimum_points"
                  :min="0" 
                  :precision="0" 
                  :step="10" 
                  placeholder="请输入积分使用最低门槛">
                  <template #append>积分</template>
                </el-input-number>
              </el-form-item>
            </el-col>
          </el-row> -->
          
          <!-- 表单按钮 -->
          <el-form-item>
            <el-button type="primary" @click="saveSettings">保存配置</el-button>
            <el-button @click="resetForm">重置</el-button>
          </el-form-item>
        </el-form>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import * as pointApi from '../../api/point';

// 加载状态
const loading = ref(true);

// 积分设置数据
const settings = ref({
  room_fee_point_ratio: 100,
  system_consumption_point_ratio: 100,
  mall_discount_ratio: 10,
  mall_minimum_points: 100,
  daily_checkin_points: 10
});

// 积分商城抵扣比例：积分:1元
const mallDiscountPoints = ref(10);

// 原始设置数据，用于重置
const originalSettings = ref({});

// 获取积分设置
async function fetchSettings() {
  loading.value = true;
  try {
    const response = await pointApi.getPointSettings();
    if (response.code === 200) {
      // 将从后端获取的小数转换为整数百分比显示
      const formattedData = {
        ...response.data,
        room_fee_point_ratio: Math.round(response.data.room_fee_point_ratio * 100),
        system_consumption_point_ratio: Math.round(response.data.system_consumption_point_ratio * 100)
      };
      settings.value = formattedData;
      // 保存原始设置用于重置
      originalSettings.value = { ...formattedData };
      
      // 处理积分商城抵扣比例，转换为积分：1元形式
      // 后端存储的是 1元 = x积分，例如：0.1 表示 1元=10积分
      const discountRatio = response.data.mall_discount_ratio;
      if (discountRatio > 0) {
        mallDiscountPoints.value = Math.round(1 / discountRatio);
      }
    }
  } catch (error) {
    ElMessage.error('获取积分设置失败：' + (error.message || '未知错误'));
    console.error('获取积分设置失败:', error);
  } finally {
    loading.value = false;
  }
}

// 保存积分设置
// eslint-disable-next-line no-unused-vars
async function saveSettings() {
  try {
    // 将积分商城抵扣比例从 积分:1元 转换为 1元=x积分
    // 例如：10:1 表示 10积分=1元，转换为小数 0.1
    const discountRatio = 1 / mallDiscountPoints.value;
    
    // 将前端输入的整数百分比转换为小数存储
    const saveData = {
      ...settings.value,
      room_fee_point_ratio: settings.value.room_fee_point_ratio / 100,
      system_consumption_point_ratio: settings.value.system_consumption_point_ratio / 100,
      mall_discount_ratio: discountRatio
    };
    const response = await pointApi.updatePointSettings(saveData);
    if (response.code === 200) {
      ElMessage.success('积分设置保存成功');
      // 更新原始设置
      originalSettings.value = { ...settings.value };
    }
  } catch (error) {
    ElMessage.error('保存积分设置失败：' + (error.message || '未知错误'));
    console.error('保存积分设置失败:', error);
  }
}

// 重置表单
// eslint-disable-next-line no-unused-vars
function resetForm() {
  settings.value = { ...originalSettings.value };
}

// 页面加载时获取设置
onMounted(() => {
  fetchSettings();
});
</script>

<style scoped>
.points-settings-container {
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.content-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.card-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.card-content {
  padding: 20px 0;
}

.member-form {
  margin-top: 20px;
}

.member-form .el-form-item {
  margin-bottom: 25px;
}

/* 规则说明样式 */
.rule-description {
  color: #606266;
  font-size: 14px;
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f0f9ff;
  border-left: 4px solid #3b82f6;
  border-radius: 0 4px 4px 0;
}

/* 带后缀的输入框样式 */
.input-with-suffix {
  position: relative;
  display: inline-block;
  width: 100%;
}

.suffix-input {
  width: calc(100% - 40px);
  border-right: none;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.input-suffix {
  position: absolute;
  right: 15;
  top: 0;
  height: 100%;
  padding: 0 10px;
  line-height: 32px;
  color: #606266;
  /* background-color: #f5f7fa; */
  /* border: 1px solid #dcdfe6; */
  /* border-left: none; */
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

/* 比例输入容器样式 */
.ratio-input-container {
  width: 100%;
}

/* 比例输入包装器样式 */
.ratio-input-wrapper {
  display: flex;
  align-items: center;
  width: 100%;
}

/* 积分输入框样式 */
.points-input {
  width: 60%;
  border-right: none;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

/* 比例分隔符样式 */
.ratio-separator {
  padding: 0 10px;
  color: #606266;
  font-size: 16px;
  font-weight: bold;
  line-height: 32px;
}

/* 固定金额显示样式 */
.fixed-amount {
  height: 32px;
  padding: 0 10px;
  line-height: 32px;
  color: #606266;
  border-left: none;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

/* 比例说明样式 */
.ratio-description {
  color: #909399;
  font-size: 12px;
  margin-top: 5px;
  margin-left: 5px;
}
</style>